import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, Tooltip } from '@mui/material';
import { activeItem } from 'store/reducers/menu';
import { useTheme } from '@mui/material/styles';

const NavItem = ({ item, level }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { drawerOpen, openItem } = useSelector((state) => state.menu);

  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('lg'));


  let itemTarget = '_self';

  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />) };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch(activeItem({ openItem: [id] }));
  };

  const isSelected = openItem.findIndex((id) => id === item.id) > -1;

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1.25rem' : '1.25rem',
        color: !openItem ? '#FFFFFF' : '#000',
        transition: 'color 0.3s ease',
      }}
    />
  ) : null;

  useEffect(() => {
    if (pathname.includes(item.url)) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
  }, [pathname, dispatch, item.url, item.id]);

  const textColor = 'text.primary';
  const iconSelectedColor = 'white';

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      onClick={() => itemHandler(item.id)}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level * 28}px` : 1.5,
        py: !drawerOpen && level === 1 ? 1.25 : 1,
        ...(drawerOpen && {
          '&:hover': {
            bgcolor: '#7E00D1',
          },
          '&.Mui-selected': {
            bgcolor: '#7E00D1',
            color: iconSelectedColor,
            '&:hover': {
              color: iconSelectedColor,
              bgcolor: '#7E00D1',
            },
          },
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: '#7E00D1',
          },
          '&.Mui-selected': {
            '&:hover': {
              bgcolor: '#7E00D1',
            },
            bgcolor: '#7E00D1',
          },
        }),
      }}
    >

      <ListItemIcon
        sx={{
          minWidth: 28,
          color: isSelected ? iconSelectedColor : textColor,
          ...(!drawerOpen && {
            borderRadius: 1.5,
            width: 36,
            height: 36,
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              bgcolor: 'secondary.lighter'
            }
          }),
          ...(!drawerOpen &&
            isSelected && {
            bgcolor: 'primary.lighter',
            '&:hover': {
              bgcolor: 'primary.lighter'
            }
          })
        }}
      >
        {(!drawerOpen && !matchDownMD) ? (
          <Tooltip
            title={item?.title}
            placement="right"
            arrow
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8]
                    }
                  }
                ]
              }
            }}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#ffffff',
                  color: '#4B0082',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
                },
              },
              arrow: {
                sx: {
                  color: '#ffffff',
                },
              },
            }}
          >
            {itemIcon}
          </Tooltip>
        ) : (
          itemIcon
        )}
      </ListItemIcon>

      {drawerOpen && (
        <ListItemText
          primary={
            <Typography
              variant="h6"
              sx={{
                color: isSelected ? iconSelectedColor : "#36454F",
                fontWeight: "600",
                '&:hover': {
                  color: 'white',
                  fontWeight: "600"
                },
              }}
            >
              {item.title}
            </Typography>
          }
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
};

export default NavItem;